import { LEVEL_MODE, UnknownActuator } from "constant";
import { get, has, startCase } from "lodash";
import { getDisplayedActuatorMode } from "pages/events/EventDetails";
import { memo } from "react";
import styledConst from "styles";
import { CustomEChartParams } from "types/Charts";
import { isFourWiredMotorControllerApp, formatBinaryActuatorYAxisLabel } from "utils/functions";
import BasicSensorChart from "./BasicSensorChart";

export const getMode = ({ sensorConfigRes, sensorType = "" }: any) => {
    if (!has(sensorConfigRes, "CMD.mode")) return "Unknown";
    // Added for backward compatibility: Generic Analog Output created by gateway using old config may still be "LEVEL" mode instead of "ANALOG"
    if (sensorType.toLowerCase() === "generic analog output") return "-";
    const configMode = getDisplayedActuatorMode(
        get(sensorConfigRes, "CMD.mode", LEVEL_MODE)
    );

    return configMode || "-";
};

const checkIsFourWiredMotor = (
    attributes: any,
    gatewaysConfig: any
): boolean => {
    if (!attributes?.length) return false;

    const { gateway_id, device_id, said } = attributes[0];
    const sensor = gatewaysConfig?.[gateway_id]?.find(
        ({ UID }: { UID: string }) => UID === device_id
    );

    if (!sensor) return false;

    return isFourWiredMotorControllerApp(
        sensor.OBJ,
        get(sensor, `APP[${said}].APPID`)
    );
};

function ActuatorChart(props: any) {
    const { attributes } = props.panel;
    const { gatewaysConfig } = props;
    const isFourWiredMotor = checkIsFourWiredMotor(attributes, gatewaysConfig);

    return (
        <BasicSensorChart
            {...{
                ...props,
                customEChartData: ({
                    liveData,
                    chartState,
                    liveTime,
                    liveDate,
                    sensorUnit,
                    sensorType,
                }: CustomEChartParams) => {
                    const sensorConfigRes = chartState?.sensorConfigRes || {};
                    const modeText =
                        get(sensorConfigRes, "CMD.mode", "") !== "MOTOR"
                            ? `\n{s3|Mode: }{s5|${getMode({
                                  sensorConfigRes,
                                  sensorType,
                              })}}`
                            : "";
                    return [
                        {
                            path: "title.subtext",
                            value: `{s6|${startCase(
                                sensorType
                            )}}${modeText}\n{s2|${
                                (isNaN(liveData) || liveData === "NaN"
                                    ? "-"
                                    : liveData) + (sensorUnit || "")
                            }}{s1|    ${liveDate + " " + liveTime}}`,
                        },
                        {
                            path: "yAxis.axisLabel.formatter",
                            value: (value: any) => {
                                return formatBinaryActuatorYAxisLabel(value, sensorType || "");
                            },
                        },
                    ];
                },
                customEChartStyle: (chartColor: string) => {
                    return [
                        {
                            path: "title.subtextStyle.rich",
                            value: {
                                s1: {
                                    color: styledConst.Primary_Blue_4,
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    height: 25,
                                },
                                s2: {
                                    color: chartColor,
                                    fontSize: 22,
                                    fontWeight: "bold",
                                },
                                s3: {
                                    color: styledConst.Primary_Blue_4,
                                    fontFamily: "Roboto",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    height: 25,
                                },
                                s4: {
                                    fontSize: 6,
                                },
                                s5: {
                                    color: styledConst.Primary_Blue_4,
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    height: 25,
                                },
                                s6: {
                                    color: styledConst.Primary_Blue_4,
                                    fontFamily: "Roboto",
                                    fontWeight: 400,
                                    fontSize: 14,
                                    height: 25,
                                },
                            },
                        },
                    ];
                },
                displayName: (chartState) => {
                    return get(
                        chartState,
                        "sensorConfigRes.sensor_name",
                        UnknownActuator
                    );
                },
                onDelete: props.handleOnChangeDashboardUUID,
                isFourWiredMotor,
                isActuator: true,
            }}
        />
    );
}

export default memo(ActuatorChart);
